import React from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";

export default function RefundPolicy() {
  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Refund Policy"
        description="Refund Policy for NextDayRefund.com — when purchases are refundable and how to request a refund. Operated by Affordable Advertising Solutions LLC, Glen Allen, Virginia."
        canonical="/refund-policy"
        noIndex={false}
      />
      <Navbar />
      <div className="section-container py-12 max-w-4xl">
        <div className="mb-8">
          <div className="text-xs font-bold uppercase tracking-wider text-[#003366] mb-3">Legal</div>
          <h1 className="text-4xl font-black text-[#003366] mb-2">Refund Policy</h1>
          <p className="text-[#64748B] text-sm">
            Last updated: September 17, 2026 &nbsp;|&nbsp; NextDayRefund.com &mdash; Operated by Affordable Advertising Solutions LLC &nbsp;|&nbsp; Glen Allen, Henrico County, Virginia
          </p>
        </div>

        <div className="prose max-w-none space-y-8 text-[#334155]">

          <section>
            <h2 className="text-xl font-bold text-[#0F172A] mb-3 pb-2 border-b border-slate-200">What This Policy Covers</h2>
            <p>This Refund Policy applies to all purchases made on NextDayRefund.com, including exclusive ZIP code claims (single ZIP or bulk purchases of multiple ZIPs) and job postings on our Jobs Board.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#0F172A] mb-3 pb-2 border-b border-slate-200">General Rule: Purchases Are Final Once Activated</h2>
            <p>Once a ZIP code claim or job posting has been activated, the purchase is final. We do not issue refunds for:</p>
            <ul className="list-disc ml-6 mt-3 space-y-1">
              <li>Change of mind after activation</li>
              <li>Unused time remaining on an active listing or job posting</li>
              <li>A lack of leads, clicks, or results</li>
              <li>Suspension or removal of a listing or job posting for violating our Terms of Service</li>
            </ul>
          </section>

          <section className="bg-[#2ECC71]/5 border-2 border-[#2ECC71] rounded-xl p-6">
            <h2 className="text-xl font-bold text-[#0F172A] mb-3">Full Refunds Will Be Issued For</h2>
            <ul className="list-disc ml-6 space-y-2">
              <li><strong>Duplicate charges</strong> for the same purchase.</li>
              <li><strong>Billing errors</strong>, such as being charged the wrong amount.</li>
              <li><strong>Purchases that could not be activated</strong> &mdash; for example, a ZIP code that had already been claimed by another advertiser at the time of purchase.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#0F172A] mb-3 pb-2 border-b border-slate-200">How to Request a Refund</h2>
            <p>
              Email <a href="mailto:support@nextdayrefund.com" className="text-[#003366] hover:underline">support@nextdayrefund.com</a> or
              call <a href="tel:+18048583074" className="text-[#003366] hover:underline">(804) 858-3074</a> within
              30 days of the charge. Please include:
            </p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>The email address on your account</li>
              <li>The date and amount of the charge</li>
              <li>The ZIP code or job title associated with the purchase</li>
            </ul>
            <p className="mt-3">We respond to refund requests within 1 business day. Approved refunds are issued to the original payment method; banks typically post them within 5&ndash;10 business days.</p>
          </section>

          <section className="bg-amber-50 border border-amber-200 rounded-xl p-6">
            <p className="font-semibold text-[#0F172A] mb-1">Please contact us before filing a dispute with your bank.</p>
            <p>Most billing issues are resolved the same day. Charges appear on your card statement as <strong>NEXTDAYREFUND</strong>.</p>
          </section>

          <div className="text-center pt-4 pb-8">
            <Link to="/terms" className="text-[#003366] font-semibold hover:underline mr-6" data-testid="view-terms-from-refund-link">View Terms &amp; Conditions</Link>
            <Link to="/contact" className="text-[#003366] font-semibold hover:underline mr-6" data-testid="view-contact-from-refund-link">Contact Us</Link>
            <Link to="/" className="text-[#64748B] hover:underline" data-testid="back-home-from-refund">Back to Home</Link>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
